import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MagnifyingGlass, ArrowsClockwise, Eye, Shield, TreeStructure, WarningCircle, CloudArrowDown } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsistencyBadge = ({ label }) => {
  if (!label) return <span className="text-[#98A2B3] text-xs">-</span>;
  const consistent = label === "Consistent";
  return (
    <Badge className={consistent ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"}>
      {label}
    </Badge>
  );
};

export default function BooManagementPage() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [siteOptions, setSiteOptions] = useState([]);
  const [fetchId, setFetchId] = useState("");
  const [fetching, setFetching] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadRecords = async (searchValue, siteValue) => {
    setLoading(true);
    try {
      const params = {};
      if (searchValue) params.search = searchValue;
      if (siteValue && siteValue !== "all") params.site_id = siteValue;
      const { data } = await axios.get(`${API}/admin/boo`, { params });
      setRecords(data.records || []);
    } catch (err) {
      toast.error("Could not load Bill of Operations list", { description: err?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
    axios.get(`${API}/admin/known-sites`).then(({ data }) => setSiteOptions(data.sites || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadRecords(search, siteFilter), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, siteFilter]);

  // Sep 27 2026, user's explicit ask: "fetch all the BOO from sap...
  // give a sync button so if new BOO created in sap you fetch it".
  // SAP's own bulk-list service for BOOs is blocked by a Basis-side
  // config gap on this tenant (live-tested, see boo_service.
  // sync_new_from_cache docstring) - this pulls in every BOO ID this
  // app has ever seen attached to a real Production Order but hasn't
  // fetched yet, via the SAME working single-BOO SAP call "Fetch from
  // SAP" already uses.
  const syncNew = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/admin/boo/sync`);
      if (data.synced.length > 0) {
        toast.success(`Synced ${data.synced.length} new BOO(s) from SAP`, { description: data.synced.join(", ") });
      } else {
        toast.info("No new BOOs to sync", { description: `${data.already_had} already up to date` });
      }
      if (data.failed.length > 0) {
        toast.error(`${data.failed.length} BOO(s) failed to sync`, { description: data.failed.map((f) => `${f.boo_id}: ${f.error}`).join("; ") });
      }
      loadRecords(search, siteFilter);
    } catch (err) {
      toast.error("Sync failed", { description: err?.response?.data?.detail });
    } finally {
      setSyncing(false);
    }
  };

  const fetchFromSap = async () => {
    const id = fetchId.trim();
    if (!id) return;
    setFetching(true);
    try {
      await axios.get(`${API}/admin/boo/${encodeURIComponent(id)}`);
      toast.success(`Fetched ${id} from SAP`);
      setFetchId("");
      loadRecords(search, siteFilter);
      navigate(`/admin/boo/${encodeURIComponent(id)}`);
    } catch (err) {
      toast.error("Fetch from SAP failed", { description: err?.response?.data?.detail });
    } finally {
      setFetching(false);
    }
  };

  const refreshOne = async (booId) => {
    setRefreshingId(booId);
    try {
      await axios.post(`${API}/admin/boo/${encodeURIComponent(booId)}/refresh`);
      toast.success(`Refreshed ${booId} from SAP`);
      loadRecords(search, siteFilter);
    } catch (err) {
      toast.error("Refresh failed", { description: err?.response?.data?.detail });
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Bill of Operations</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="bg-white border-b border-[#D0D5DD] p-3 flex flex-wrap items-center gap-3 shrink-0" data-testid="boo-search-bar">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search BOO ID or Description..."
            className="h-9 w-64 pl-7 text-sm"
            data-testid="boo-search-input"
          />
        </div>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="h-9 w-40 text-sm" data-testid="boo-site-filter">
            <SelectValue placeholder="All Sites" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="boo-site-filter-option-all">All Sites</SelectItem>
            {siteOptions.map((s) => (
              <SelectItem key={s} value={s} data-testid={`boo-site-filter-option-${s}`}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={syncNew}
          disabled={syncing}
          variant="outline"
          className="h-9 text-xs border-[#0E7C86] text-[#0E7C86] hover:bg-[#E7F5F6]"
          data-testid="boo-sync-button"
          title="Fetch any BOO from SAP this app hasn't seen yet (discovered via Production Orders already run in this app)"
        >
          <CloudArrowDown size={13} className={`mr-1.5 ${syncing ? "animate-bounce" : ""}`} /> {syncing ? "Syncing..." : "Sync from SAP"}
        </Button>
        <div className="w-px h-6 bg-[#D0D5DD]" />
        <div className="flex items-center gap-2">
          <Input
            value={fetchId}
            onChange={(e) => setFetchId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchFromSap()}
            placeholder="Exact BOO ID e.g. BLCODE_ALLITEM_RESTRIKE"
            className="h-9 w-72 text-sm"
            data-testid="boo-fetch-id-input"
          />
          <Button onClick={fetchFromSap} disabled={fetching || !fetchId.trim()} className="h-9 bg-[#0E7C86] hover:bg-[#0B6570] text-xs" data-testid="boo-fetch-from-sap-button">
            <TreeStructure size={13} className="mr-1.5" /> {fetching ? "Fetching..." : "Fetch from SAP"}
          </Button>
        </div>
        <span className="text-xs text-[#98A2B3] ml-auto">
          "Sync from SAP" pulls in any BOO already used in a Production Order here but not yet fetched. For a brand-new BOO never used yet, type its exact ID above.
        </span>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <div className="text-sm text-[#667085] p-6 text-center">Loading...</div>
        ) : records.length === 0 ? (
          <div className="text-sm text-[#667085] p-10 text-center border border-dashed border-[#D0D5DD] rounded-lg bg-white" data-testid="boo-empty-state">
            No Bill of Operations fetched yet. Type an exact BOO ID above and click "Fetch from SAP" to get started.
          </div>
        ) : (
          <div className="bg-white border border-[#D0D5DD] rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="boo-list-table">
              <thead className="bg-[#F9FAFB] border-b border-[#D0D5DD] text-[#475467] text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">BOO ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Description</th>
                  <th className="text-left px-3 py-2 font-semibold">Site</th>
                  <th className="text-left px-3 py-2 font-semibold">Qty</th>
                  <th className="text-left px-3 py-2 font-semibold">Unit</th>
                  <th className="text-left px-3 py-2 font-semibold">Operations</th>
                  <th className="text-left px-3 py-2 font-semibold">Planning</th>
                  <th className="text-left px-3 py-2 font-semibold">Execution</th>
                  <th className="text-right px-3 py-2 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.boo_id} className="border-b border-[#F2F4F7] hover:bg-[#F9FAFB]" data-testid={`boo-row-${r.boo_id}`}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-[#101828]">{r.boo_id}</td>
                    <td className="px-3 py-2 text-[#344054]">{r.description || "-"}</td>
                    <td className="px-3 py-2">
                      {r.site_id ? <Badge className="bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]">{r.site_id}</Badge> : "-"}
                    </td>
                    <td className="px-3 py-2">{r.quantity ?? "-"}</td>
                    <td className="px-3 py-2">{r.unit || "-"}</td>
                    <td className="px-3 py-2">
                      <span className={r.operations_mapped < r.operations_count ? "text-[#B54708] font-medium" : "text-[#027A48] font-medium"}>
                        {r.operations_mapped}/{r.operations_count} mapped
                      </span>
                      {r.operations_mapped < r.operations_count && (
                        <WarningCircle size={13} className="inline ml-1 text-[#B54708]" />
                      )}
                    </td>
                    <td className="px-3 py-2"><ConsistencyBadge label={r.planning_consistency_label} /></td>
                    <td className="px-3 py-2"><ConsistencyBadge label={r.execution_consistency_label} /></td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs mr-1.5"
                        onClick={() => navigate(`/admin/boo/${encodeURIComponent(r.boo_id)}`)}
                        data-testid={`boo-view-button-${r.boo_id}`}
                      >
                        <Eye size={12} className="mr-1" /> View
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        disabled={refreshingId === r.boo_id}
                        onClick={() => refreshOne(r.boo_id)}
                        data-testid={`boo-refresh-button-${r.boo_id}`}
                      >
                        <ArrowsClockwise size={12} className={`mr-1 ${refreshingId === r.boo_id ? "animate-spin" : ""}`} /> Refresh
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
